import React, { useEffect, useLayoutEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useSelector } from 'react-redux'

import { IRootReducerState } from 'state/rootReducer'
import { useIsPortal } from 'utils/Subscription/PermissionSelector'

const ShopChangeRefreshProvider: React.FC = props => {
  const queryClient = useQueryClient()
  const currentShopID = useSelector((state: IRootReducerState) => state.auth.currentShop)
  const [renderedShopID, setRenderedShopID] = useState(currentShopID)
  const [remountKey, setRemountKey] = useState(0)

  const isPortal = useIsPortal()
  // Only a genuine switch between two already-known shops needs a cache wipe + remount.
  // Going from no shop yet to the first real shop ID (e.g. right after login, before employee
  // data has seeded currentShop) is not a switch and must not blank/remount the whole app.
  const isSwitchingShop = !!renderedShopID && renderedShopID !== currentShopID

  useEffect(() => {
    document.body.classList.toggle('b2b-portal', isPortal)
  }, [isPortal])

  useLayoutEffect(() => {
    if (renderedShopID === currentShopID) {
      return
    }

    if (isSwitchingShop) {
      // Clear the query cache to prevent cached data
      // showing up after changing your shop.
      // Otherwise we might produce some very hard to trace bugs.
      queryClient.clear()
      setRemountKey(key => key + 1)
    }

    setRenderedShopID(currentShopID)
  }, [currentShopID, isSwitchingShop, queryClient, renderedShopID])

  if (isSwitchingShop) {
    return null
  }

  return <React.Fragment key={remountKey}>{props.children}</React.Fragment>
}

export default ShopChangeRefreshProvider
